import { useState, KeyboardEvent, ClipboardEvent } from "react";
import { X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  maxSuggestions?: number;
  className?: string;
  /** Visual tone of chips. */
  tone?: "default" | "danger";
  "aria-label"?: string;
}

/** Split pasted/typed text into clean, de-duplicated items. */
export function splitChips(text: string): string[] {
  return text.split(/\n|,|;|\t/).map((x) => x.trim()).filter(Boolean);
}

function addUnique(list: string[], items: string[]): string[] {
  const seen = new Set(list.map((x) => x.toLowerCase()));
  const out = [...list];
  for (const it of items) {
    const k = it.toLowerCase();
    if (!seen.has(k)) { seen.add(k); out.push(it); }
  }
  return out;
}

export function ChipInput({ value, onChange, placeholder = "Type and press Enter", suggestions = [], maxSuggestions = 6, className, tone = "default", ...rest }: ChipInputProps) {
  const [draft, setDraft] = useState("");
  const list = value ?? [];

  const commit = (text = draft) => {
    const items = splitChips(text);
    if (items.length) onChange(addUnique(list, items));
    setDraft("");
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); }
    else if (e.key === "Backspace" && !draft && list.length) onChange(list.slice(0, -1));
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (/[\n,;\t]/.test(text)) { e.preventDefault(); commit(draft + text); }
  };

  const lower = new Set(list.map((x) => x.toLowerCase()));
  const q = draft.trim().toLowerCase();
  const [focused, setFocused] = useState(false);
  const visibleSuggestions = (list.length >= 6 || (list.length > 0 && !focused)) ? [] : suggestions
    .filter((s) => !lower.has(s.toLowerCase()) && (!q || s.toLowerCase().includes(q)))
    .slice(0, maxSuggestions);

  const chipCls = tone === "danger"
    ? "border-destructive/30 bg-destructive/10 text-destructive"
    : "border-sage/30 bg-sage-muted text-sage";

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 focus-within:border-primary">
        {list.map((item) => (
          <span key={item} className={cn("inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium", chipCls)}>
            <span className="truncate">{item}</span>
            <button type="button" onClick={() => onChange(list.filter((x) => x !== item))} aria-label={`Remove ${item}`}
              className="rounded-full opacity-60 hover:opacity-100">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onPaste={onPaste}
          onFocus={() => setFocused(true)}
          onBlur={() => { commit(); setTimeout(() => setFocused(false), 150); }}
          placeholder={list.length ? "Add more…" : placeholder}
          aria-label={rest["aria-label"]}
          className="min-w-[120px] flex-1 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      {visibleSuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {visibleSuggestions.map((s) => (
            <button key={s} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(addUnique(list, [s])); setDraft(""); }}
              className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary">
              <Plus className="h-3 w-3" />{s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
